import type { Feature, Position } from 'geojson';

import type maplibregl from 'maplibre-gl';

// Coincident-marker separation. One database row can project a POI and a leak at the same
// coordinates ("leak as reported" clears the leak location, so the leak falls back to the POI
// point), and duplicate worklist imports have produced root POIs sharing an exact coordinate.
// Those markers must stay individually visible and clickable, so exactly-coincident features
// are annotated with their position in the stack and separated at render time with a
// data-driven icon-offset. The geometry itself is NEVER changed: source geometry feeds
// supercluster, so moving points would silently alter cluster membership and counts, whereas
// icon-offset is applied after clustering, at symbol placement.
//
// Only exact coordinate equality forms a stack. Near-but-distinct markers are a different
// problem (symbol collision), handled by icon-allow-overlap and layer ordering, not by
// offsetting truthful positions.

/** Properties added to every member of an exactly-coincident group. */
export interface StackAnnotation {
    /** 0-based position within the stack, ordered by feature `id` for stable placement. */
    stack_index: number;
    /** Total members sharing this exact coordinate. */
    stack_size: number;
}

/** Largest stack observed in production data (two duplicate roots, each with a coincident leak). */
export const MAX_EXPECTED_STACK_SIZE = 4;

function coordinateKey(position: Position): string {
    // Exact equality on the raw doubles: coincident markers arise from copied column values
    // (identical doubles end-to-end), so no tolerance/rounding is wanted — a tolerance would
    // start merging genuinely distinct nearby markers.
    return `${String(position[0])},${String(position[1])}`;
}

function featureId(feature: Feature): string {
    const id: unknown = feature.properties?.['id'];
    return typeof id === 'string' ? id : '';
}

// POI entities take the left-most fan positions so a stack reads "POI, then its leaks"
// left-to-right. A missing entity_type counts as a POI (the wire contract's tolerance rule).
function entityRank(feature: Feature): number {
    return feature.properties?.['entity_type'] === 'leak' ? 1 : 0;
}

/**
 * Annotates exactly-coincident Point features with `stack_index`/`stack_size` properties.
 *
 * Within a stack, members are ordered semantically first — POI entities before leak
 * entities — then by their `id` property (lexicographic) within a family, so larger stacks
 * stay deterministic and the same feature always receives the same fan position across
 * refetches and sessions. Features that share a coordinate with nothing else are returned
 * untouched (same object — no per-feature allocation on the common path). Non-Point features
 * pass through untouched.
 *
 * The input array is not mutated; annotated features get fresh `properties` objects.
 */
export function computeStackOffsets(features: Feature[]): Feature[] {
    const groups = new Map<string, number[]>();
    features.forEach((feature, index) => {
        if (feature.geometry.type !== 'Point') return;
        const key = coordinateKey(feature.geometry.coordinates);
        const group = groups.get(key);
        if (group) {
            group.push(index);
        } else {
            groups.set(key, [index]);
        }
    });

    const result = features.slice();
    for (const group of groups.values()) {
        if (group.length < 2) continue;
        const ordered = group
            .slice()
            .sort(
                (a, b) =>
                    entityRank(result[a]) - entityRank(result[b]) ||
                    featureId(result[a]).localeCompare(featureId(result[b])),
            );
        ordered.forEach((featureIndex, stackIndex) => {
            const original = result[featureIndex];
            result[featureIndex] = {
                ...original,
                properties: {
                    ...original.properties,
                    stack_index: stackIndex,
                    stack_size: ordered.length,
                },
            };
        });
    }
    return result;
}

/**
 * Horizontal fan x-offset (in icon pixels) for one member of a stack, centred on the true
 * coordinate: a pair sits at ±separation/2, a triple at -separation/0/+separation, etc.
 */
export function stackFanX(stackIndex: number, stackSize: number, separationPx: number): number {
    return (stackIndex - (stackSize - 1) / 2) * separationPx;
}

/**
 * Builds the data-driven `icon-offset` expression separating stacked markers.
 *
 * MapLibre's expression language has no dynamic array constructor — `['literal', ...]` arrays
 * are constants — so the per-member offset cannot be computed arithmetically inside the
 * expression. Instead every (stack_size, stack_index) combination up to `maxStackSize` is
 * enumerated as its own `['literal', [x, y]]` branch. With stacks capped at 4 members that is
 * nine branches, which is why enumeration is viable at all.
 *
 * `tipOffset` is the layer's existing anchor correction (the pin tip sits above the canvas
 * bottom, so the icon is nudged down to land the tip on the coordinate); the fan x-offset is
 * composed with it rather than replacing it. Unstacked features fall through to plain
 * `tipOffset`.
 */
export function buildStackIconOffsetExpression(
    separationPx: number,
    tipOffset: readonly [number, number],
    maxStackSize: number = MAX_EXPECTED_STACK_SIZE,
): maplibregl.ExpressionSpecification {
    const branches: unknown[] = [];
    for (let size = 2; size <= maxStackSize; size++) {
        for (let index = 0; index < size; index++) {
            branches.push(
                ['all', ['==', ['get', 'stack_size'], size], ['==', ['get', 'stack_index'], index]],
                ['literal', [tipOffset[0] + stackFanX(index, size, separationPx), tipOffset[1]]],
            );
        }
    }
    return ['case', ...branches, ['literal', [tipOffset[0], tipOffset[1]]]] as maplibregl.ExpressionSpecification;
}
